<template>
  <layout-public>
    <page-meta />
    <page-hero
      :crumbs="[
        { label: t('Home'), href: route('index') },
        { label: t('Services'), href: route('index') + '#services' },
        { label: t('Web design') }
      ]"
      :title="t('Web Design')"
      :lede="t('Website design, development, hosting, business email and ongoing support, all bundled together in one flat monthly plan with nothing at all to pay.')"
    />

    <service-block
      variant="plain"
      :kicker="t('Designed, built and looked after.')"
      :title="t('A custom website that stays fast, secure and online.')"
      :lede="t('Most agencies simply hand over the files and a login, then bill you again whenever something breaks. We do the exact opposite and stay involved.')"
      :aside-title="t('In the price, not an add-on.')"
      :aside-items="includes"
    >
      <div class="action-row">
        <app-button href="#pricing" icon="arrow-down">
          {{ t('See the plans') }}
        </app-button>
      </div>
    </service-block>

    <feature />

    <pricing />

    <one-off-plan-band href="#one-off-plans" />

    <section class="one-off dark">
      <div class="container">
        <div class="one-off-grid">
          <div class="one-off-pitch" data-aos="fade-right">
            <section-title
              align="left"
              tone="dark"
              :kicker="t('One-time web design')"
              :title="t('Pay once, own the website.')"
              :lede="t('No monthly subscription and no minimum contract. You purchase the design and build once, and the source code files are handed over at launch.')"
            >
              <div class="action-row">
                <app-button href="#one-off-plans" tone="dark" icon="arrow-down">
                  {{ t('See one-off plans') }}
                </app-button>
              </div>
            </section-title>
          </div>

          <panel-list
            as="aside"
            class="one-off-includes"
            tone="dark"
            :title="t('Included in the one-off price.')"
            :items="oneOffPoints"
          />
        </div>
      </div>
    </section>

    <plan-table
      block-id="one-off-plans"
      :kicker="t('Paying less does not mean getting less.')"
      :title="t('One-off web design plans')"
      :lede="t('One-time website design for anyone who would rather not subscribe. You pay once, retain the source files, and we host the website for a year.')"
      :price-label="t('Price')"
      :price-hint="t('What you pay once for the plan.')"
      :columns="oneOffColumns"
      :rows="oneOffRows"
      :remarks="oneOffRemarks"
    />

    <portfolio :projects="projects" />

    <faq-list
      :kicker="t('Before you decide')"
      :title="t('Frequently asked questions')"
      :lede="t('These are the questions we get asked most often by businesses comparing a monthly web subscription with the cost of a one-off website build.')"
      :items="faqs"
      accordion-id="web-design-faq"
    />
  </layout-public>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The one-off band is page-local and its $ground-dark ground comes from the global
   `section.dark`. What is added here is a slow blue aurora behind the panel
   plus the stacking context that keeps it under the content, so the band reads
   as a living ground rather than a flat fill. Nothing here waits on scroll:
   content reveal is a separate pass, so the section only animates in place. */
.one-off {
  overflow: hidden;
  position: relative;

  &::before {
    background:
      radial-gradient(42rem 26rem at 16% 18%, $tint-primary-strong, transparent 70%),
      radial-gradient(34rem 24rem at 86% 84%, rgba($accent-blue, .16), transparent 72%);
    content: '';
    inset: -25% -12%;
    pointer-events: none;
    position: absolute;
    animation: one-off-drift 24s $ease infinite alternate;
  }

  > .container {
    position: relative;
    z-index: 1;
  }
}

@keyframes one-off-drift {
  from {
    transform: translate3d(-2%, -1.5%, 0) scale(1);
  }

  to {
    transform: translate3d(2%, 1.5%, 0) scale(1.06);
  }
}

.one-off-grid {
  align-items: start;
  display: grid;
  gap: 2.5rem;
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 992px) {
    gap: 3.5rem;
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  }
}

.one-off-pitch {
  /* Center this column against the taller includes panel, not its own text. */
  align-self: center;
  text-align: left;
}

/* The action rows this page used to declare (.one-off-actions, .brief-actions)
   are gone: they were two of the six copies of the same lift, the same arrow
   nudge and the same 1.75rem gap. The button owns its own hover now, and the
   gap comes from the global .action-row. */

/* The one-off include panel is PanelList, and the light brief one is now
   ServiceBlock's own aside. Both were the same "mark, title, body" list written
   separately here, with the same hairline rules, the same icon lift and the
   same beam, so the panel owns that anatomy instead.

   The brief used to be a second two-column grid declared on this page and
   nested inside ServiceBlock's slot. That put it inside `.service-main`, whose
   column is capped at 40rem so prose does not run long, so the grid could never
   fill the container and the panel stopped a third of the width short of the
   right edge. It is the same `service-split` the other two service intros use
   now, which is what ServiceBlock's own comment says it was modelled on. */
</style>

<script>
import { computed } from 'vue';
import { faArrowDown, faCode, faEnvelope, faServer, faShieldHalved, faWallet } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import PageMeta from '@/Components/PageMeta.vue';
import AppButton from '@/Components/AppButton.vue';
import FaqList from '@/Components/FaqList.vue';
import Feature from '@/Pages/Index/Sections/Feature.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import OneOffPlanBand from '@/Components/OneOffPlanBand.vue';
import PanelList from '@/Components/PanelList.vue';
import PageHero from '@/Components/PageHero.vue';
import PlanTable from '@/Components/PlanTable.vue';
import Portfolio from '@/Pages/Index/Sections/Portfolio.vue';
import Pricing from '@/Pages/Index/Sections/Pricing.vue';
import SectionTitle from '@/Components/SectionTitle.vue';
import ServiceBlock from '@/Components/ServiceBlock.vue';
import { scrollToId } from '@/smoothScroll';

FontAwesomeLibrary.add(faArrowDown, faCode, faEnvelope, faServer, faShieldHalved, faWallet);

export default {
  components: {
    AppButton,
    FaqList,
    Feature,
    PageMeta,
    LayoutPublic,
    OneOffPlanBand,
    PanelList,
    PageHero,
    PlanTable,
    Portfolio,
    Pricing,
    SectionTitle,
    ServiceBlock
  },

  props: {
    /* The featured projects, from `PortfolioCatalog::featured()`. This page
       shows the same selected-work band the home page does, so the two cannot
       disagree about which work is shown first or about what a card says. */
    projects: {
      type: Array,
      default: () => []
    }
  },

  /* The page is linked to with anchors from other pages (the one-off cross-sell
     band on the home page points at #one-off-plans), so a direct load of a
     hashed URL lands on the section rather than the top of the page. */
  mounted() {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) {
      return;
    }

    var id = hash.slice(1);
    this.$nextTick(() => {
      window.setTimeout(() => scrollToId(id), 80);
    });
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'What you pay once for the plan.': 'What you pay once for the plan.',
          'One-off web design plans are a one-time payment, not a subscription.': 'One-off web design plans are a one-time payment, not a subscription.',
          'Hosting, domain and SSL are included for the first year, then continued hosting is RM 338 a year.': 'Hosting, domain and SSL are included for the first year, then continued hosting is RM 338 a year.',
          'Build time is about a week for Startup ONE, two to four weeks for Growth ONE, and three to five weeks for Pro ONE.': 'Build time is about a week for Startup ONE, two to four weeks for Growth ONE, and three to five weeks for Pro ONE.',
          'Veximus holds and manages the domain while the website is hosted with us.': 'Veximus holds and manages the domain while the website is hosted with us.',
          'If you do not renew, we take the hosted site down, you keep the source files, and we help move the domain over.': 'If you do not renew, we take the hosted site down, you keep the source files, and we help move the domain over.',
          'A full store with payments and stock is scoped on the Growth plan or the Custom plan.': 'A full store with payments and stock is scoped on the Growth plan or the Custom plan.',
          'Website design, development, hosting, business email and ongoing support, all bundled together in one flat monthly plan with nothing at all to pay.': 'Website design, development, hosting, business email and ongoing support, all bundled together in one flat monthly plan with nothing at all to pay.',
          'Most agencies simply hand over the files and a login, then bill you again whenever something breaks. We do the exact opposite and stay involved.': 'Most agencies simply hand over the files and a login, then bill you again whenever something breaks. We do the exact opposite and stay involved.',
          'Designed, built and looked after.': 'Designed, built and looked after.',
          'One-time web design': 'One-time web design',
          'Pay once, own the website.': 'Pay once, own the website.',
          'No monthly subscription and no minimum contract. You purchase the design and build once, and the source code files are handed over at launch.': 'No monthly subscription and no minimum contract. You purchase the design and build once, and the source code files are handed over at launch.',
          'See one-off plans': 'See one-off plans',
          'One payment, nothing after launch': 'One payment, nothing after launch',
          'No monthly fee and no 12-month minimum. After launch the only recurring cost is hosting, and only if you want us to keep the website online.': 'No monthly fee and no 12-month minimum. After launch the only recurring cost is hosting, and only if you want us to keep the website online.',
          'The source files are yours': 'The source files are yours',
          'The source code repository goes with the build, so the website can move with you whenever you need it to.': 'The source code repository goes with the build, so the website can move with you whenever you need it to.',
          'First year hosted and secured': 'First year hosted and secured',
          'Hosting, domain and SSL are covered for the first year. From the second year it is RM 338 a year if you stay with us.': 'Hosting, domain and SSL are covered for the first year. From the second year it is RM 338 a year if you stay with us.',
          'A custom website that stays fast, secure and online.': 'A custom website that stays\nfast, secure and **online**.',
          'In the price, not an add-on.': 'In the price, not an add-on.',
          'Included in the one-off price.': 'Included in the one-off price.',
          'Your domain and hosting': 'Your domain and hosting',
          'HTTPS on every page': 'HTTPS on every page',
          'Email on your own domain': 'Email on your own domain',
          'Can I buy the site outright and stop paying?': 'Can I buy the site outright and stop paying?',
          'Can you rebuild a site that is currently on Wix or another website builder?': 'Can you rebuild a site that is currently on Wix or another website builder?',
          'Web Design': 'Web Design',
          'These are the questions we get asked most often by businesses comparing a monthly web subscription with the cost of a one-off website build.': 'These are the questions we get asked most often by businesses comparing a monthly web subscription with the cost of a one-off website build.',
          'Growth ONE plan': 'Growth ONE plan',
          'How do I choose the Startup, Growth or Custom plans?': 'How do I choose the Startup, Growth or Custom plans?',
          'How is a one-off web design plan different?': 'How is a one-off web design plan different?',
          'Is a payment gateway included?': 'Is a payment gateway included?',
          'Is the site mobile friendly?': 'Is the site mobile friendly?',
          'One-off web design plans': '**One-off** web design plans',
          'One-time payment': 'one-time payment',
          'One-time website design for anyone who would rather not subscribe. You pay once, retain the source files, and we host the website for a year.': 'One-time website design for anyone who would rather not subscribe. You pay once, retain the source files, and we host the website for a year.',
          'Pro ONE plan': 'Pro ONE plan',
          'Startup ONE plan': 'Startup ONE plan',
          'Your own top-level domain and hosting that grows with your traffic, so there is no separate bill and no cPanel for you to babysit.': 'Your own top-level domain and hosting that grows with your traffic, so there is no separate bill and no cPanel for you to babysit.',
          'The padlock that tells a visitor it is safe to type. Security should never be a line item you have to approve.': 'The padlock that tells a visitor it is safe to type. Security should never be a line item you have to approve.',
          'Quotes and invoices stop coming from a Gmail address. You get business mail on your own name, the way a real business should.': 'Quotes and invoices stop coming from a Gmail address. You get business mail on your own name, the way a real business should.',
          'What am I actually buying?': 'What am I actually buying?',
          'What if my invoice is overdue?': 'What if my invoice is overdue?',
          'Who writes the words on the pages?': 'Who writes the words on the pages?',
          '1 page': '1 page',
          '1 year, then RM 338 a year': '1 year, then RM 338 a year',
          '3 to 5 pages': '3 to 5 pages',
          '6 to 12 pages': '6 to 12 pages',
          'How many pages your website can have, including subpages.': 'How many pages your website can have, including subpages.',
          'faq-buy': 'A website we design and launch, then a web subscription plan while we keep it online. Hosting, a top-level domain, SSL, CDN, anti-DDoS, analytics and business email stay included while you subscribe. Web subscription plans have a minimum 12-month contract that starts after the project launch. A Custom plan development fee is invoiced separately when that plan applies. Prices exclude SST.',
          'faq-choose': 'Startup plan is one main page, one email, about one week. Growth plan is seven main pages plus unlimited subpages, five emails, weekly backups, two to four weeks. Growth plan is the usual fit for a company profile. Custom plan is unlimited pages, daily backups, MySQL, a source repository, a development fee and a date set in the project proposal, then RM 118 per month billed annually.',
          'faq-friendly': 'Yes. Every web design plan is built to read on a phone. An application you install from an app store is a different product and is quoted separately.',
          'faq-gateway': 'Web design plans do not automatically include a payment gateway. We add Stripe, eGHL, FPX or similar as scoped work. Bank and gateway fees are charged by those providers, not by us.',
          'faq-logo': 'Logo design is not inside web design plan fees. We can launch with a wordmark and your colours. A logo is quoted on the Brand Presence page.',
          'faq-oneoff-diff': 'You get the source code. Hosting, domain and SSL are included for one year, then RM 338 a year if you continue. Startup ONE plan is 1 page, Growth ONE plan is 3 to 5 pages, Pro ONE plan is 6 to 12 pages. No CDN, email, analytics, support, backups or MySQL.',
          'faq-outright': 'Yes. That is a one-off web design plan: a one-time payment for the design, with one year of hosting, domain and SSL.',
          'faq-overdue': 'There is a one-month grace period after the invoice is overdue. If we still do not receive payment or an update from you in that month, the service ends.',
          'faq-rebuild': 'Yes. We rebuild it. We do not complete another vendor\'s unfinished builder project. These plans are not a WordPress theme rescue.',
          'faq-words': 'You supply the facts, services, prices you want shown, and photos if you have them. We place that copy in the layout. A full copywriting job is quoted if you want us to write from a blank page.'
        },
        'zh-MY': {
          'What you pay once for the plan.': '一次付清，这就是配套的全部费用。',
          'One-off web design plans are a one-time payment, not a subscription.': '一次性网页设计配套只收一次费用，不是订阅制。',
          'Hosting, domain and SSL are included for the first year, then continued hosting is RM 338 a year.': '首年的托管、域名和 SSL 全都包含，第二年起若继续由我们托管，每年 RM 338。',
          'Build time is about a week for Startup ONE, two to four weeks for Growth ONE, and three to five weeks for Pro ONE.': '制作时间：Startup ONE 约一周，Growth ONE 约两到四周，Pro ONE 约三到五周。',
          'Veximus holds and manages the domain while the website is hosted with us.': '网站由我们托管的期间，域名由 Veximus 代为持有和管理。',
          'If you do not renew, we take the hosted site down, you keep the source files, and we help move the domain over.': '若不续约，我们会把托管中的网站下线，源代码仍然归您，域名转移我们也会协助。',
          'A full store with payments and stock is scoped on the Growth plan or the Custom plan.': '含付款和库存的完整网店，属于 Growth 或 Custom 配套的范围。',
          'Website design, development, hosting, business email and ongoing support, all bundled together in one flat monthly plan with nothing at all to pay.': '网站设计、开发、托管、企业邮箱与长期技术支援，全部打包在一个简单固定的月费里，除此之外没有任何额外收费，收费透明，绝无任何隐藏费用。',
          'Most agencies simply hand over the files and a login, then bill you again whenever something breaks. We do the exact opposite and stay involved.': '很多公司把文件和登录账号交给您之后，一出问题就再收费。我们的做法刚好相反，从设计、开发到上线，全程由我们负责到底，绝不会放着不管。',
          'Designed, built and looked after.': '从设计到维护，全都包了。',
          'One-time web design': '一次性网页设计',
          'Pay once, own the website.': '付一次，网站就是您的。',
          'No monthly subscription and no minimum contract. You purchase the design and build once, and the source code files are handed over at launch.': '没有月费，也没有最短合约。设计和开发只付一次，源代码在上线时完整交给您，第一年的托管、域名与 SSL 也一并附上，网站完全属于您。',
          'See one-off plans': '看看一次性配套',
          'One payment, nothing after launch': '只付一次，上线后不再收费',
          'No monthly fee and no 12-month minimum. After launch the only recurring cost is hosting, and only if you want us to keep the website online.': '没有月费，也没有 12 个月绑约。上线之后唯一会再花的钱是托管，而且只在您要我们继续帮您把网站挂在线上时才需要付。',
          'The source files are yours': '源代码是您的',
          'The source code repository goes with the build, so the website can move with you whenever you need it to.': '源代码仓库会随项目一起交付，网站要跟着您走，随时都行。',
          'First year hosted and secured': '首年托管与安全防护全包',
          'Hosting, domain and SSL are covered for the first year. From the second year it is RM 338 a year if you stay with us.': '首年的托管、域名和 SSL 都由我们负责。第二年起若继续给我们托管，每年 RM 338。',
          'A custom website that stays fast, secure and online.': '始终保持快速、安全、稳定的\n**随时在线**的定制网站。',
          'In the price, not an add-on.': '本来就含在价格里，不是附加项目。',
          'Included in the one-off price.': '这些都含在一次性费用里。',
          'Your domain and hosting': '自己的域名和托管',
          'HTTPS on every page': '每一页都有 HTTPS',
          'Email on your own domain': '用自己域名的公司邮箱',
          'Can I buy the site outright and stop paying?': '可以一次买断网站，之后就不再付费吗？',
          'Can you rebuild a site that is currently on Wix or another website builder?': '我现在的网站架在 Wix 或其他建站平台上，你们能重做吗？',
          'Web Design': '网页设计',
          'These are the questions we get asked most often by businesses comparing a monthly web subscription with the cost of a one-off website build.': '企业在比较每月网页订阅与一次性建站的费用时，最常问我们的问题，都整理在这一页，您想知道的答案应该全都在里面了，让您可以一次全部看清。',
          'Growth ONE plan': 'Growth ONE 配套',
          'How do I choose the Startup, Growth or Custom plans?': 'Startup、Growth、Custom 这三个配套该怎么选？',
          'How is a one-off web design plan different?': '一次性网页设计配套有什么不同？',
          'Is a payment gateway included?': '配套里包含支付网关吗？',
          'Is the site mobile friendly?': '网站在手机上看可以吗？',
          'One-off web design plans': '**一次性**网页设计配套',
          'One-time payment': '一次付清',
          'One-time website design for anyone who would rather not subscribe. You pay once, retain the source files, and we host the website for a year.': '不想订阅的话，就选一次性网页设计。一次付清，源代码归您，第一年由我们帮您托管，之后要不要续约，也绝对不会自动扣款，一切都由您说了算。',
          'Pro ONE plan': 'Pro ONE 配套',
          'Startup ONE plan': 'Startup ONE 配套',
          'Your own top-level domain and hosting that grows with your traffic, so there is no separate bill and no cPanel for you to babysit.': '您自己的顶级域名，加上会跟着流量一起扩充的托管，不必另外收账单，也不用自己盯着 cPanel。',
          'The padlock that tells a visitor it is safe to type. Security should never be a line item you have to approve.': '那把让访客敢放心输入的锁。安全从来不该是需要您另外批准才加的项目。',
          'Quotes and invoices stop coming from a Gmail address. You get business mail on your own name, the way a real business should.': '报价单和发票不再从 Gmail 寄出。您用的是自己公司的邮箱，这才像一门正经生意。',
          'What am I actually buying?': '我实际上买到的是什么？',
          'What if my invoice is overdue?': '如果账单逾期会怎样？',
          'Who writes the words on the pages?': '页面上的文字是谁写的？',
          '1 page': '1 个页面',
          '1 year, then RM 338 a year': '1 年，之后每年 RM 338',
          '3 to 5 pages': '3 到 5 个页面',
          '6 to 12 pages': '6 到 12 个页面',
          'How many pages your website can have, including subpages.': '您的网站可以有多少个页面，包含子页面。',
          'faq-buy': '您买到的是一个由我们负责设计和上线的网站，之后再用网页订阅配套让它在线上持续运作。订阅期间，托管、顶级域名、SSL、CDN、DDoS 防护、数据分析和企业邮箱都一直包含在内。网页订阅配套在项目上线之后才开始，最短合约为 12 个月。若选用 Custom 配套，开发费会另外开单。价格不含 SST。',
          'faq-choose': 'Startup 是 1 个主页面、1 个邮箱，约一周完成。Growth 是 7 个主页面加不限量子页面、5 个邮箱、每周备份，约两到四周。公司简介型的网站通常选 Growth。Custom 是不限页面、每日备份、MySQL、源代码仓库，开发费和完工日期以项目提案为准，之后每月 RM 118、按年收费。',
          'faq-friendly': '可以。每一套网页设计都是照着手机阅读来做的。从应用商店下载的那种手机 App 是另一回事，需要另外报价。',
          'faq-gateway': '网页设计配套不含支付网关。Stripe、eGHL、FPX 这类服务会另外按范围加入。银行和网关的手续费由那些供应商收，不是我们收。',
          'faq-logo': '商标设计不含在网页设计配套的费用里。我们可以先用文字标志和您的品牌色把网站做上线。商标设计请到品牌形象那页看，会另外报价。',
          'faq-oneoff-diff': '源代码会给您。托管、域名和 SSL 包含一年，之后若继续由我们托管，每年 RM 338。Startup ONE 是 1 个页面，Growth ONE 是 3 到 5 个页面，Pro ONE 是 6 到 12 个页面。不含 CDN、邮箱、数据分析、技术支援、备份和 MySQL。',
          'faq-outright': '可以，那就是一次性网页设计配套：设计费一次付清，包含一年的托管、域名和 SSL。',
          'faq-overdue': '账单逾期后有一个月的宽限期。如果这一个月内还是没收到付款，也没收到您的消息，服务就会终止。',
          'faq-rebuild': '会。我们会重做。但我们不接手其他公司做到一半的建站项目，这些配套也不是用来抢救 WordPress 主题的。',
          'faq-words': '您提供要放上网站的资料、服务、想显示的价格，有照片就一起给。我们负责把这些内容排进版面。如果您要我们从零开始写文案，那会另外报价。'
        },
        'ms-MY': {
          'What you pay once for the plan.': 'Bayar sekali, itu sahaja kos pelan ini.',
          'One-off web design plans are a one-time payment, not a subscription.': 'Pelan reka bentuk web sekali bayar dikenakan sekali sahaja, bukan langganan.',
          'Hosting, domain and SSL are included for the first year, then continued hosting is RM 338 a year.': 'Hosting, domain dan SSL sudah termasuk untuk tahun pertama. Tahun berikutnya, jika mahu terus dihoskan dengan kami, RM 338 setahun.',
          'Build time is about a week for Startup ONE, two to four weeks for Growth ONE, and three to five weeks for Pro ONE.': 'Masa siap: Startup ONE sekitar seminggu, Growth ONE dua hingga empat minggu, dan Pro ONE tiga hingga lima minggu.',
          'Veximus holds and manages the domain while the website is hosted with us.': 'Selagi laman web dihoskan dengan kami, domain dipegang dan diuruskan oleh Veximus.',
          'If you do not renew, we take the hosted site down, you keep the source files, and we help move the domain over.': 'Jika anda tidak memperbaharui, kami turunkan laman yang dihoskan, fail sumber tetap milik anda, dan kami bantu pindahkan domain.',
          'A full store with payments and stock is scoped on the Growth plan or the Custom plan.': 'Kedai lengkap dengan pembayaran dan stok termasuk dalam skop pelan Growth atau Custom.',
          'Website design, development, hosting, business email and ongoing support, all bundled together in one flat monthly plan with nothing at all to pay.': 'Reka bentuk laman web, pembangunan, hosting, e-mel perniagaan dan sokongan berterusan, semuanya digabungkan dalam satu bayaran bulanan yang tetap.',
          'Most agencies simply hand over the files and a login, then bill you again whenever something breaks. We do the exact opposite and stay involved.': 'Kebanyakan agensi hanya menyerahkan fail dan login, kemudian mengenakan bayaran lagi setiap kali ada masalah. Kami buat sebaliknya sepenuhnya.',
          'Designed, built and looked after.': 'Direka, dibina dan dijaga sepenuhnya.',
          'One-time web design': 'Reka bentuk web sekali bayar',
          'Pay once, own the website.': 'Bayar sekali, laman web itu milik anda.',
          'No monthly subscription and no minimum contract. You purchase the design and build once, and the source code files are handed over at launch.': 'Tiada langganan bulanan dan tiada kontrak minimum. Anda bayar sekali untuk reka bentuk dan pembinaan, dan fail sumber diserahkan semasa pelancaran.',
          'See one-off plans': 'Lihat pelan sekali bayar',
          'One payment, nothing after launch': 'Satu bayaran sahaja, tiada caj selepas pelancaran',
          'No monthly fee and no 12-month minimum. After launch the only recurring cost is hosting, and only if you want us to keep the website online.': 'Tiada yuran bulanan dan tiada ikatan minimum 12 bulan. Selepas pelancaran, satu-satunya kos berulang ialah hosting, dan itu pun hanya jika anda mahu kami terus mengekalkan laman web anda dalam talian.',
          'The source files are yours': 'Fail sumber milik anda',
          'The source code repository goes with the build, so the website can move with you whenever you need it to.': 'Repositori kod sumber diserahkan sekali dengan projek, jadi laman web boleh berpindah bersama anda bila-bila masa anda perlukan.',
          'First year hosted and secured': 'Tahun pertama dihoskan dan dilindungi',
          'Hosting, domain and SSL are covered for the first year. From the second year it is RM 338 a year if you stay with us.': 'Hosting, domain dan SSL ditanggung untuk tahun pertama. Dari tahun kedua, RM 338 setahun jika anda terus bersama kami.',
          'A custom website that stays fast, secure and online.': 'Laman web tersuai yang sentiasa\npantas, selamat dan **dalam talian**.',
          'In the price, not an add-on.': 'Memang sudah termasuk dalam harga, bukan caj tambahan.',
          'Included in the one-off price.': 'Semua ini sudah termasuk dalam harga sekali bayar.',
          'Your domain and hosting': 'Domain dan hosting milik anda sendiri',
          'HTTPS on every page': 'HTTPS pada setiap halaman',
          'Email on your own domain': 'E-mel pada domain anda sendiri',
          'Can I buy the site outright and stop paying?': 'Bolehkah saya beli laman web ini terus dan berhenti membayar?',
          'Can you rebuild a site that is currently on Wix or another website builder?': 'Laman web saya sekarang di Wix atau pembina laman lain. Boleh anda bina semula?',
          'Web Design': 'Reka Bentuk Web',
          'These are the questions we get asked most often by businesses comparing a monthly web subscription with the cost of a one-off website build.': 'Soalan yang paling kerap kami terima daripada perniagaan yang membandingkan langganan web bulanan dengan kos binaan sekali bayar, semuanya dijawab.',
          'Growth ONE plan': 'Pelan Growth ONE',
          'How do I choose the Startup, Growth or Custom plans?': 'Bagaimana saya hendak pilih antara pelan Startup, Growth dan Custom?',
          'How is a one-off web design plan different?': 'Apa bezanya pelan reka bentuk web sekali bayar?',
          'Is a payment gateway included?': 'Adakah gerbang pembayaran termasuk?',
          'Is the site mobile friendly?': 'Boleh ke laman web ini dibuka dengan selesa di telefon?',
          'One-off web design plans': 'Pelan Reka Bentuk Web **Sekali Bayar**',
          'One-time payment': 'bayaran sekali',
          'One-time website design for anyone who would rather not subscribe. You pay once, retain the source files, and we host the website for a year.': 'Kalau tidak mahu langganan, pilih reka bentuk web sekali bayar. Bayar sekali, fail sumber jadi milik anda, dan tahun pertama kami hoskan untuk anda.',
          'Pro ONE plan': 'Pelan Pro ONE',
          'Startup ONE plan': 'Pelan Startup ONE',
          'Your own top-level domain and hosting that grows with your traffic, so there is no separate bill and no cPanel for you to babysit.': 'Domain peringkat atasan milik anda sendiri, dan hosting yang membesar ikut trafik anda. Tiada bil berasingan, dan tiada cPanel yang perlu anda jaga sendiri.',
          'The padlock that tells a visitor it is safe to type. Security should never be a line item you have to approve.': 'Kunci yang memberitahu pelawat laman ini selamat untuk ditaip. Keselamatan tidak sepatutnya jadi perkara yang perlu anda luluskan dahulu.',
          'Quotes and invoices stop coming from a Gmail address. You get business mail on your own name, the way a real business should.': 'Sebut harga dan invois tidak lagi keluar dari alamat Gmail. Anda dapat e-mel perniagaan pada nama sendiri, seperti sepatutnya sebuah perniagaan.',
          'What am I actually buying?': 'Sebenarnya saya beli apa?',
          'What if my invoice is overdue?': 'Bagaimana kalau invois saya tertunggak?',
          'Who writes the words on the pages?': 'Siapa yang tulis teks pada halaman?',
          '1 page': '1 halaman',
          '1 year, then RM 338 a year': '1 tahun, kemudian RM 338 setahun',
          '3 to 5 pages': '3 hingga 5 halaman',
          '6 to 12 pages': '6 hingga 12 halaman',
          'How many pages your website can have, including subpages.': 'Berapa banyak halaman yang boleh ada pada laman web anda, termasuk subhalaman.',
          'faq-buy': 'Anda dapat laman web yang kami reka dan lancarkan, kemudian pelan langganan web yang mengekalkannya dalam talian. Sepanjang langganan, hosting, domain peringkat atasan, SSL, CDN, anti-DDoS, analitis dan e-mel perniagaan semuanya kekal termasuk. Pelan langganan web terikat kontrak minimum 12 bulan yang bermula selepas pelancaran projek. Untuk pelan Custom, yuran pembangunan diinvois secara berasingan. Harga tidak termasuk SST.',
          'faq-choose': 'Pelan Startup: satu halaman utama, satu e-mel, kira-kira seminggu. Pelan Growth: tujuh halaman utama tambah subhalaman tanpa had, lima e-mel, sandaran mingguan, dua hingga empat minggu. Untuk laman profil syarikat, Growth biasanya yang paling sesuai. Pelan Custom: halaman tanpa had, sandaran harian, MySQL, repositori kod sumber, yuran pembangunan dan tarikh yang ditetapkan dalam cadangan projek, kemudian RM 118 sebulan dibilkan setiap tahun.',
          'faq-friendly': 'Boleh. Setiap pelan reka bentuk web kami dibina untuk dibaca dengan selesa pada telefon. Aplikasi yang anda muat turun dari gedung aplikasi pula produk lain, dan disebut harga secara berasingan.',
          'faq-gateway': 'Pelan reka bentuk web tidak termasuk gerbang pembayaran secara automatik. Kami boleh tambah Stripe, eGHL, FPX atau yang seumpamanya sebagai kerja berasingan. Yuran bank dan gerbang pembayaran dikenakan oleh penyedia masing-masing, bukan oleh kami.',
          'faq-logo': 'Reka bentuk logo tidak termasuk dalam yuran pelan reka bentuk web. Kami boleh lancarkan dahulu dengan wordmark dan warna brand anda. Untuk logo, sila lihat halaman Kehadiran Jenama, harganya berasingan.',
          'faq-oneoff-diff': 'Kod sumber akan diserahkan kepada anda. Hosting, domain dan SSL termasuk selama satu tahun, kemudian RM 338 setahun jika anda terus bersama kami. Startup ONE ialah 1 halaman, Growth ONE 3 hingga 5 halaman, dan Pro ONE 6 hingga 12 halaman. Tiada CDN, e-mel, analitis, sokongan, sandaran atau MySQL.',
          'faq-outright': 'Boleh. Itulah pelan reka bentuk web sekali bayar: bayaran sekali untuk reka bentuk, dengan hosting, domain dan SSL untuk satu tahun.',
          'faq-overdue': 'Ada tempoh tangguh satu bulan selepas invois tertunggak. Jika dalam bulan itu kami masih tidak menerima bayaran atau khabar daripada anda, perkhidmatan akan ditamatkan.',
          'faq-rebuild': 'Boleh. Kami akan bina semula. Tetapi kami tidak menyambung projek pembina laman yang tergantung daripada vendor lain. Pelan ini juga bukan untuk menyelamatkan tema WordPress.',
          'faq-words': 'Anda berikan fakta, perkhidmatan, harga yang mahu dipaparkan, dan foto jika ada. Kami susun kandungan itu ke dalam reka letak. Jika anda mahu kami tulis dari kosong, itu disebut harga secara berasingan.'
        }
      }
    });

    const yes = { type: 'yes' };
    const no = { type: 'no' };

    const oneOffPoints = computed(() => [
      {
        icon: 'wallet',
        title: t('One payment, nothing after launch'),
        body: t('No monthly fee and no 12-month minimum. After launch the only recurring cost is hosting, and only if you want us to keep the website online.')
      },
      {
        icon: 'code',
        title: t('The source files are yours'),
        body: t('The source code repository goes with the build, so the website can move with you whenever you need it to.')
      },
      {
        icon: 'server',
        title: t('First year hosted and secured'),
        body: t('Hosting, domain and SSL are covered for the first year. From the second year it is RM 338 a year if you stay with us.')
      }
    ]);

    const includes = computed(() => [
      {
        icon: 'server',
        title: t('Your domain and hosting'),
        body: t('Your own top-level domain and hosting that grows with your traffic, so there is no separate bill and no cPanel for you to babysit.')
      },
      {
        icon: 'shield-halved',
        title: t('HTTPS on every page'),
        body: t('The padlock that tells a visitor it is safe to type. Security should never be a line item you have to approve.')
      },
      {
        icon: 'envelope',
        title: t('Email on your own domain'),
        body: t('Quotes and invoices stop coming from a Gmail address. You get business mail on your own name, the way a real business should.')
      }
    ]);

    const oneOffColumns = computed(() => [
      {
        name: t('Startup ONE plan'),
        audience: t('For new businesses'),
        icon: 'rocket',
        blurb: t('Best for startup businesses and small companies that need to showcase their ideas.'),
        price: 'RM 1,688',
        unit: t('One-time payment')
      },
      {
        name: t('Growth ONE plan'),
        audience: t('For growing businesses'),
        icon: 'chart-line',
        blurb: t('Great for growing businesses who want a professional image and improve their branding.'),
        price: 'RM 2,488',
        unit: t('One-time payment')
      },
      {
        name: t('Pro ONE plan'),
        audience: t('For custom builds'),
        icon: 'pen-ruler',
        badge: t('Most Popular'),
        blurb: t('Best suited for businesses who want more customisations to fit their exact requirements.'),
        price: 'RM 3,488',
        unit: t('One-time payment'),
        hit: true
      }
    ]);

    const oneOffRows = computed(() => [
      { label: t('Pages'), hint: t('How many pages your website can have, including subpages.'), cells: [{ type: 'text', value: t('1 page') }, { type: 'text', value: t('3 to 5 pages') }, { type: 'text', value: t('6 to 12 pages') }] },
      { label: t('Revisions'), hint: t('How many times we can still change the look and the wording.'), cells: [{ type: 'text', value: t('Until first launch') }, { type: 'text', value: t('Until first launch') }, { type: 'text', value: t('Until first launch') }] },
      { label: t('Hosting, domain, SSL'), hint: t('We keep the site online, give it a web address, and add the lock in the browser.'), cells: [{ type: 'text', value: t('1 year, then RM 338 a year') }, { type: 'text', value: t('1 year, then RM 338 a year') }, { type: 'text', value: t('1 year, then RM 338 a year') }] },
      { label: t('CDN and anti-DDoS'), hint: t('Helps pages open faster, and helps the site stay up if someone overloads it.'), cells: [no, no, no] },
      { label: t('Business email'), hint: t('Your own email using the website name, like hello@yourshop.com.'), cells: [no, no, no] },
      { label: t('Google Analytics 4'), hint: t('A report that shows how many people visit your website.'), cells: [no, no, no] },
      { label: t('Technical support'), hint: t('You can contact us if the website has a problem or you need help.'), cells: [no, no, no] },
      { label: t('Backups'), hint: t('A saved copy of the website we can restore if something goes wrong.'), cells: [no, no, no] },
      { label: t('MySQL database'), hint: t('Where the website stores things like products, forms and member details.'), cells: [no, no, no] },
      { label: t('Source code repository'), hint: t('The files that make the website, kept so you can take them later.'), cells: [yes, yes, yes] }
    ]);

    const faqs = computed(() => [
      { q: t('What am I actually buying?'), a: t('faq-buy') },
      { q: t('How do I choose the Startup, Growth or Custom plans?'), a: t('faq-choose') },
      { q: t('Can I buy the site outright and stop paying?'), a: t('faq-outright') },
      { q: t('How is a one-off web design plan different?'), a: t('faq-oneoff-diff') },
      { q: t('Is the site mobile friendly?'), a: t('faq-friendly') },
      { q: t('Who writes the words on the pages?'), a: t('faq-words') },
      { q: t('I do not have a logo. Do you design one?'), a: t('faq-logo') },
      { q: t('Can you rebuild a site that is currently on Wix or another website builder?'), a: t('faq-rebuild') },
      { q: t('Is a payment gateway included?'), a: t('faq-gateway') },
      { q: t('What if my invoice is overdue?'), a: t('faq-overdue') }
    ]);

    const oneOffRemarks = computed(() => [
      t('One-off web design plans are a one-time payment, not a subscription.'),
      t('Hosting, domain and SSL are included for the first year, then continued hosting is RM 338 a year.'),
      t('Build time is about a week for Startup ONE, two to four weeks for Growth ONE, and three to five weeks for Pro ONE.'),
      t('Veximus holds and manages the domain while the website is hosted with us.'),
      t('If you do not renew, we take the hosted site down, you keep the source files, and we help move the domain over.')
    ]);

    return { t, includes, oneOffColumns, oneOffPoints, oneOffRows, oneOffRemarks, faqs };
  }
};
</script>
